import { cssVar } from '@toeverything/theme';
import { cssVarV2 } from '@toeverything/theme/v2';
import { Component, lazy, type ReactNode, Suspense, useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';

import { PortalAuthProvider, usePortalAuth } from './auth/portal-auth-provider';
import { PortalLayout } from './screens/portal-layout';
import { PortalErpProvider } from './services/portal-erp-provider';

// Route-level code-splitting: each screen loads its own chunk on first visit, so
// the initial portal bundle carries only the auth guard + layout chrome (both
// stay EAGER — the OIDC/PKCE flow and the nav render immediately, untouched).
const ImpactHome = lazy(() =>
  import('./screens/impact-home').then(m => ({ default: m.ImpactHome }))
);
const Orders = lazy(() =>
  import('./screens/orders').then(m => ({ default: m.Orders }))
);
const Assets = lazy(() =>
  import('./screens/assets').then(m => ({ default: m.Assets }))
);
const ItemPassport = lazy(() =>
  import('./screens/item-passport').then(m => ({ default: m.ItemPassport }))
);
const QuoteRequests = lazy(() =>
  import('./screens/quote-requests').then(m => ({ default: m.QuoteRequests }))
);
const Certificates = lazy(() =>
  import('./screens/certificates').then(m => ({ default: m.Certificates }))
);
const Activate = lazy(() =>
  import('./screens/activate').then(m => ({ default: m.Activate }))
);

// The minimal token-themed blank frame: the pre-login gate renders it full-frame,
// and it doubles as the per-route Suspense fallback while a screen chunk loads
// (consistent with the existing affordance — a themed blank, no user-facing
// string; each screen renders its own localized loading state once mounted).
function ThemedBlank() {
  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: cssVarV2('layer/background/primary'),
        color: cssVarV2('text/primary'),
        fontFamily: cssVar('fontFamily'),
      }}
    />
  );
}

// One-shot chunk-reload recovery: a redeploy orphans the old hashed screen
// chunks, so the first lazy-route load failure reloads the page ONCE to pick up
// the new asset manifest. The sessionStorage flag arms the one-shot; a
// successful boot clears it (module top-level, once) so a future redeploy can
// recover again. The flag's boot-time value is captured BEFORE the clear — it is
// what the boundary consults, so a second consecutive failure (this boot IS the
// reload retry) renders the blank frame instead of looping reloads.
const CHUNK_RELOAD_FLAG = 'pipe-chunk-reload';
const bootWasChunkReloadRetry = (() => {
  try {
    const was = sessionStorage.getItem(CHUNK_RELOAD_FLAG) !== null;
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    return was;
  } catch {
    return false;
  }
})();

/** The tiny shared boundary around each lazy route (no user-visible strings —
 *  it renders the existing {@link ThemedBlank} while reloading / when the
 *  one-shot reload already ran). */
class ChunkErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch() {
    if (!bootWasChunkReloadRetry) {
      try {
        sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
      } catch {
        // sessionStorage unavailable — fall through to the blank frame.
      }
      window.location.reload();
    }
  }

  override render() {
    if (this.state.failed) {
      return <ThemedBlank />;
    }
    return this.props.children;
  }
}

/**
 * Gate every portal screen behind a Bearer access token. With no token we kick
 * off the Keycloak Authorization-Code + PKCE redirect (CLAUDE.md #6). 13b/13c
 * render the real screens inside the guard.
 */
function AuthGuard({ children }: { children: ReactNode }) {
  const { token, login } = usePortalAuth();

  // No token → kick off the Keycloak Authorization-Code + PKCE redirect. This
  // navigates away, so there is no interim screen (and no user-facing string to
  // hardcode); 13b can layer a branded splash on top if desired.
  useEffect(() => {
    if (!token) {
      // Best-effort: a failed redirect kickoff leaves the blank interim panel
      // (the console breadcrumb is the only trace).
      login().catch(e => console.error('portal auth: pkce flow failed', e));
    }
  }, [token, login]);

  if (!token) {
    return <ThemedBlank />;
  }

  return <>{children}</>;
}

/** Completes the `/callback` redirect, then returns to the impact home. */
function CallbackRoute() {
  const { handleCallback } = usePortalAuth();
  const navigate = useNavigate();

  useEffect(() => {
    handleCallback()
      .then(() => navigate('/', { replace: true }))
      // Best-effort: a failed exchange stays on /callback (same as before —
      // the console breadcrumb is the only trace).
      .catch(e => console.error('portal auth: pkce flow failed', e));
  }, [handleCallback, navigate]);

  return null;
}

/**
 * The portal route table. The `/callback` route is intentionally OUTSIDE the
 * {@link AuthGuard} (it runs the token exchange). The authed screens nest under
 * the {@link PortalLayout} (nav + <Outlet/>) so 13c's screens share the chrome.
 */
function PortalRoutes() {
  return (
    <Routes>
      <Route path="/callback" element={<CallbackRoute />} />
      {/* Invite activation: UNAUTHENTICATED by design (the visitor has no login
          yet — the single-use invite token in the URL is the authorization). */}
      <Route
        path="/activate"
        element={
          <ChunkErrorBoundary>
            <Suspense fallback={<ThemedBlank />}>
              <Activate />
            </Suspense>
          </ChunkErrorBoundary>
        }
      />
      <Route
        element={
          <AuthGuard>
            <PortalLayout />
          </AuthGuard>
        }
      >
        <Route
          path="/"
          element={
            <ChunkErrorBoundary>
              <Suspense fallback={<ThemedBlank />}>
                <ImpactHome />
              </Suspense>
            </ChunkErrorBoundary>
          }
        />
        <Route
          path="/orders"
          element={
            <ChunkErrorBoundary>
              <Suspense fallback={<ThemedBlank />}>
                <Orders />
              </Suspense>
            </ChunkErrorBoundary>
          }
        />
        <Route
          path="/assets"
          element={
            <ChunkErrorBoundary>
              <Suspense fallback={<ThemedBlank />}>
                <Assets />
              </Suspense>
            </ChunkErrorBoundary>
          }
        />
        <Route
          path="/items/:id"
          element={
            <ChunkErrorBoundary>
              <Suspense fallback={<ThemedBlank />}>
                <ItemPassport />
              </Suspense>
            </ChunkErrorBoundary>
          }
        />
        <Route
          path="/certificates"
          element={
            <ChunkErrorBoundary>
              <Suspense fallback={<ThemedBlank />}>
                <Certificates />
              </Suspense>
            </ChunkErrorBoundary>
          }
        />
        <Route
          path="/requests"
          element={
            <ChunkErrorBoundary>
              <Suspense fallback={<ThemedBlank />}>
                <QuoteRequests />
              </Suspense>
            </ChunkErrorBoundary>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export const App = () => {
  return (
    <PortalAuthProvider>
      <PortalErpProvider>
        <BrowserRouter>
          <PortalRoutes />
        </BrowserRouter>
      </PortalErpProvider>
    </PortalAuthProvider>
  );
};
